import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  type AreaName,
  bulletsToLines,
  linesToBullets,
  type Section,
  type SectionContent,
  type SectionKey,
} from "./checkin-insights";

// Prototype: lists past 6 bullets (+1, so "Show all" never hides one) fold.
const BULLET_LIMIT = 6;

const AREA_COLORS: Record<AreaName, string> = {
  training: "var(--training-day)",
  nutrition: "var(--nutrition)",
  daily: "var(--tracking)",
  goals: "var(--target-goals)",
};

type Translate = TFunction<"athlete-portal">;

export const sectionText = (
  key: SectionKey,
  section: Section,
  t: Translate
) => {
  const title = t(`checkinInsights.sections.${key}`);
  if ("text" in section) return `${title}\n${section.text}`;
  const list = (bullets: string[]) =>
    bullets.map((bullet) => `• ${bullet}`).join("\n");
  if ("areas" in section)
    return `${title}\n${section.areas
      .map(
        (area) =>
          `${t(`checkinInsights.areas.${area.name}`)}\n${list(area.bullets)}`
      )
      .join("\n\n")}`;
  return `${title}\n${list(section.bullets)}`;
};

/** Edit drafts: summary text, a bullet list, or one bullet list per trend area. */
const toDraft = (section: Section): string[] => {
  if ("text" in section) return [section.text];
  if ("areas" in section)
    return section.areas.map((area) => bulletsToLines(area.bullets));
  return [bulletsToLines(section.bullets)];
};

const fromDraft = (
  section: Section,
  draft: string[]
): SectionContent | null => {
  if ("text" in section)
    return draft[0].trim() ? { text: draft[0].trim() } : null;
  if ("areas" in section) {
    const areas = section.areas.map((area, index) => ({
      name: area.name,
      bullets: linesToBullets(draft[index]),
    }));
    return areas.every((area) => area.bullets.length) ? { areas } : null;
  }
  const bullets = linesToBullets(draft[0]);
  return bullets.length ? { bullets } : null;
};

const BulletList = ({ bullets, showAll }) => {
  const folds = bullets.length > BULLET_LIMIT + 1;
  const shown = folds && !showAll ? bullets.slice(0, BULLET_LIMIT) : bullets;
  return (
    <ul>
      {shown.map((bullet, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: bullets can repeat and never reorder
        <li key={index}>{bullet}</li>
      ))}
    </ul>
  );
};

export const InsightSection = ({
  sectionKey,
  section,
  open,
  onToggle,
  onSave,
}: {
  sectionKey: SectionKey;
  section: Section;
  open: boolean;
  onToggle: () => void;
  onSave: (content: SectionContent) => Promise<void>;
}) => {
  const { t } = useTranslation("athlete-portal");
  const [showAll, setShowAll] = useState(false);
  const [draft, setDraft] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  if (!section) return null;

  const title = t(`checkinInsights.sections.${sectionKey}`);
  const edited = draft ? fromDraft(section, draft) : null;

  const save = async () => {
    if (!edited) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(edited);
      setDraft(null);
    } catch (saveError) {
      setError(saveError.message);
    }
    setSaving(false);
  };

  const editor = draft && (
    <div className="ci-edit">
      {draft.map((value, index) => {
        const area = "areas" in section ? section.areas[index] : null;
        return (
          <label key={area?.name ?? sectionKey}>
            {area && (
              <span className="area-h">
                <i
                  style={
                    { "--c": AREA_COLORS[area.name] } as React.CSSProperties
                  }
                />
                {t(`checkinInsights.areas.${area.name}`)}
              </span>
            )}
            <textarea
              value={value}
              rows={Math.min(12, Math.max(3, value.split("\n").length + 1))}
              aria-label={
                area ? t(`checkinInsights.areas.${area.name}`) : title
              }
              onChange={(event) =>
                setDraft(
                  draft.map((current, position) =>
                    position === index ? event.target.value : current
                  )
                )
              }
            />
          </label>
        );
      })}
      {!("text" in section) && (
        <span className="ci-cap">{t("checkinInsights.onePerLine")}</span>
      )}
      {error && (
        <span className="ci-error" role="alert">
          {error}
        </span>
      )}
      <div className="foot">
        <button
          type="button"
          data-button-type="secondary"
          sz="small"
          onClick={() => {
            setDraft(null);
            setError(null);
          }}
        >
          {t("checkinInsights.cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          sz="small"
          disabled={!edited || saving}
          onClick={save}
        >
          {t("checkinInsights.save")}
        </button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if ("text" in section) body = <p>{section.text}</p>;
  else if ("areas" in section)
    body = section.areas.map((area) => (
      <div className="area" key={area.name}>
        <div className="area-h">
          <i style={{ "--c": AREA_COLORS[area.name] } as React.CSSProperties} />
          {t(`checkinInsights.areas.${area.name}`)}
        </div>
        <BulletList bullets={area.bullets} showAll />
      </div>
    ));
  else
    body = (
      <>
        <BulletList bullets={section.bullets} showAll={showAll} />
        {section.bullets.length > BULLET_LIMIT + 1 && (
          <button
            type="button"
            data-button-type="text"
            sz="small"
            className="show-more"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? t("checkinInsights.showFewer")
              : t("checkinInsights.showAll", { count: section.bullets.length })}
          </button>
        )}
      </>
    );

  return (
    <div className={`ci-sec${open ? "" : " closed"}${copied ? " copied" : ""}`}>
      <div className="sec-h">
        <button
          type="button"
          className="sec-toggle"
          aria-expanded={open}
          onClick={onToggle}
        >
          <SvgIcon
            src="/img/new-icons/chevron-down.svg"
            useCurrentColor
            classNames="ci-icon chev"
          />
          <span className="t">{title}</span>
          {section.edited && (
            <span className="ci-pill default">
              {t("checkinInsights.edited")}
            </span>
          )}
        </button>
        {!draft && (
          <>
            <button
              type="button"
              data-button-type="secondary"
              data-icon-only
              aria-label={t("checkinInsights.editSection", { section: title })}
              title={t("checkinInsights.editSection", { section: title })}
              onClick={() => {
                if (!open) onToggle();
                setDraft(toDraft(section));
              }}
            >
              <SvgIcon
                src="/img/new-icons/edit-pencil.svg"
                useCurrentColor
                classNames="ci-icon"
              />
            </button>
            <button
              type="button"
              data-button-type="secondary"
              data-icon-only
              aria-label={t("checkinInsights.copySection", { section: title })}
              title={t("checkinInsights.copySection", { section: title })}
              onClick={() => {
                navigator.clipboard
                  ?.writeText(sectionText(sectionKey, section, t))
                  .catch(() => {});
                setCopied(true);
                window.setTimeout(() => setCopied(false), 700);
              }}
            >
              <SvgIcon
                src={`/img/new-icons/${copied ? "tick-circle-outline-white" : "copy"}.svg`}
                useCurrentColor
                classNames="ci-icon"
              />
            </button>
          </>
        )}
      </div>
      {open && <div className="sec-b">{draft ? editor : body}</div>}
    </div>
  );
};
